import React from 'react'
import { useTranslation } from 'react-i18next'

import { Title2 } from '@ui/Typography'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'

import { SubscriptionBundleDto } from '@model/SubscriptionBundleDto'
import { SubscriptionCartItem } from '@model/SubscriptionCart'
import { SubscriptionGroupDto } from '@model/SubscriptionGroupDto'
import { SubscriptionDetail } from '@model/SubscriptionTypes'
import {
  SubscriptionRequestType,
  UpdateSubscriptionRequest,
} from '@model/UpdateSubscriptionRequest'

import { SubscriptionBundle } from './SubscriptionBundle'
import { SubscriptionGroup } from './SubscriptionGroup'

import styles from './MarketDataPage.module.scss'

interface SubscriptionContentProps {
  subscriptionBundlesLoading: boolean
  subscriptionGroupsLoading: boolean
  subscriptionBundles: SubscriptionBundleDto[]
  subscriptionGroups: SubscriptionGroupDto[]
  subscribedBundles: SubscriptionBundleDto[]
  nonActiveSubscriptions: SubscriptionCartItem[]
  scrollToCart?: () => void
  onUpdateSubscription(data: UpdateSubscriptionRequest, selectedItem?: SubscriptionDetail): void
  onAddCartItem(
    subscriptionDetailId: string,
    subscriptionRequestTypeId: SubscriptionRequestType
  ): void
}

export const SubscriptionContent: React.FC<SubscriptionContentProps> = (props) => {
  const { t } = useTranslation()

  const { subscriptionBundlesLoading, subscriptionGroupsLoading, subscriptionBundles } = props
  const { subscribedBundles, nonActiveSubscriptions, subscriptionGroups = [] } = props
  const { scrollToCart, onUpdateSubscription, onAddCartItem } = props

  return (
    <>
      <div className={styles.products}>
        <Title2>{t('Market Data.Top Selling Bundles')}</Title2>
        <Table
          isLoading={subscriptionBundlesLoading}
          noResults={
            !subscriptionBundles.length && (
              <TableNoResults title={t('Subscriptions.No products list')} />
            )
          }
        >
          <TableHeader>
            <TableRow>
              <TableHead align='left'>{t('Market Data.Product')}</TableHead>
              <TableHead align='left'>{t('Market Data.Price')}</TableHead>
              <TableHead align='center'>{t('Market Data.Status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subscriptionBundles.map((bundle) => (
              <SubscriptionBundle
                scrollToCart={scrollToCart}
                bundle={bundle}
                subscribedBundles={subscribedBundles}
                onUpdateSubscription={onUpdateSubscription}
                onAddCartItem={onAddCartItem}
                nonActiveSubscriptions={nonActiveSubscriptions}
                key={bundle.id}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      <Title2>{t('Market Data.Standalone Products')}</Title2>
      <Table
        isLoading={subscriptionGroupsLoading}
        noResults={
          !subscriptionGroups.length && (
            <TableNoResults title={t('Subscriptions.No products list')} />
          )
        }
      >
        <TableHeader>
          <TableRow>
            <TableHead align='left'>{t('Market Data.Product')}</TableHead>
            <TableHead align='left'>{t('Market Data.Price')}</TableHead>
            <TableHead align='center'>{t('Market Data.Status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {subscriptionGroups.map((subscriptionGroup) => {
            return (
              <SubscriptionGroup
                scrollToCart={scrollToCart}
                subscribedBundles={subscribedBundles}
                subscriptionGroup={subscriptionGroup}
                onUpdateSubscription={onUpdateSubscription}
                onAddCartItem={onAddCartItem}
                nonActiveSubscriptions={nonActiveSubscriptions}
                key={subscriptionGroup.id}
              />
            )
          })}
        </TableBody>
      </Table>
    </>
  )
}
